import { useEffect, useState } from 'react';
import type { ViewportEngine } from '../../viewer/ViewportEngine';
import { fitted, initialWindow, openedOn, type Window, type Windowing } from '../../viewer/window/windowing';

/** One image's window/level as the controls see it, and the ways to move it. */
export interface WindowLevel {
  windowing: Windowing;
  window: Window;
  inverted: boolean;
  set(window: Window): void;
  invert(): void;
  /** Back to the device's window, not inverted. */
  reset(): void;
}

type Chosen = { window: Window; inverted: boolean };

/**
 * The window/level of the image each engine shows — the single view's, or
 * each of compare's panes' (user, 2026-10-16) — or null where it has nothing
 * to window (colour, 8-bit, a slide). Kept for those engines only: another
 * image opens on its own window, as the engine draws it, and nothing is
 * remembered once the image is left (decided 2026-10-15).
 */
export function useWindowLevels(engines: readonly (ViewportEngine | null)[]): (WindowLevel | null)[] {
  const [chosen, setChosen] = useState<ReadonlyMap<ViewportEngine, Chosen>>(() => new Map());
  // Only the images on screen keep theirs: one that was left (another image in
  // its pane, compare closed) is let go, and with it the engine it drew with.
  useEffect(() => {
    setChosen((prev) => {
      const kept = [...prev].filter(([e]) => engines.includes(e));
      return kept.length === prev.size ? prev : new Map(kept);
    });
  });
  return engines.map((engine) => {
    const windowing = engine?.windowing?.() ?? null;
    if (!engine || !windowing) return null;
    // Untouched, the window the engine opened on: the automatic one when chosen
    // in Settings, else the device's (windowing openingWindow).
    const current = chosen.get(engine) ?? { window: openedOn(windowing), inverted: false };
    const apply = (window: Window, inverted: boolean) => {
      // The window the engine can draw, so the readout says what is on screen.
      const drawn = fitted(window, windowing);
      engine.setWindow?.(drawn, inverted);
      setChosen((prev) => new Map(prev).set(engine, { window: drawn, inverted }));
    };
    return {
      windowing,
      window: current.window,
      inverted: current.inverted,
      set: (w) => apply(w, current.inverted),
      invert: () => apply(current.window, !current.inverted),
      reset: () => apply(initialWindow(windowing), false),
    };
  });
}
